import { fetchNotificationConfig, registerNotificationDevice } from "../services/api";
import { getHqsServiceWorkerRegistration } from "../registerSW";

function publicKeyToUint8Array(base64Value) {
  const padding = "=".repeat((4 - (base64Value.length % 4)) % 4);
  const base64 = (base64Value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const decoded = window.atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

export function browserPushCapability() {
  if (typeof window === "undefined") return { supported: false, permission: "unsupported" };
  const supported = "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
  return {
    supported,
    permission: supported ? Notification.permission : "unsupported",
  };
}

export async function inspectBrowserPush() {
  const capability = browserPushCapability();
  if (!capability.supported) return { ...capability, subscribed: false, configured: false };
  const configResult = await fetchNotificationConfig();
  const config = configResult.data || {};
  let subscribed = false;
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    subscribed = Boolean(await registration?.pushManager?.getSubscription?.());
  } catch {
    subscribed = false;
  }
  return {
    ...capability,
    configured: Boolean(config.browserPushAvailable && config.publicKey),
    inAppAvailable: Boolean(config.inAppAvailable),
    subscribed,
    error: configResult.error || null,
  };
}

export async function enableBrowserPush() {
  const capability = browserPushCapability();
  if (!capability.supported) throw new Error("Dieser Browser unterstützt Web Push nicht.");

  const configResult = await fetchNotificationConfig();
  const config = configResult.data || {};
  if (configResult.error || !config.browserPushAvailable || !config.publicKey) {
    throw new Error("Browser-Push ist serverseitig noch nicht vollständig konfiguriert.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied" ? "Browser-Benachrichtigungen wurden blockiert." : "Browser-Benachrichtigungen wurden nicht freigegeben.");
  }

  const registration = await getHqsServiceWorkerRegistration();
  if (!registration) throw new Error("Der HQS Service Worker konnte nicht registriert werden.");

  const existing = await registration.pushManager.getSubscription();
  const subscription = existing || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: publicKeyToUint8Array(config.publicKey),
  });
  const registerResult = await registerNotificationDevice(subscription.toJSON());
  if (registerResult.error || !registerResult.data?.success) {
    if (!existing) await subscription.unsubscribe().catch(() => false);
    throw new Error(registerResult.error || "Push-Abonnement konnte nicht bei Railway registriert werden.");
  }
  return { permission, subscribed: true, configured: true };
}

export async function disableBrowserPush() {
  if (!("serviceWorker" in navigator)) return { subscribed: false };
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager?.getSubscription?.();
  if (subscription) await subscription.unsubscribe();
  return { subscribed: false };
}
